/**
 * What to say, with the proof folded underneath it.
 *
 * The claim is the sentence; the findings under it are why the operator is
 * allowed to say it. Every argument the server let through cites findings that
 * resolve - it drops the rest rather than showing them - so the list under a
 * claim is never empty and never decorative.
 *
 * THE DROPPED COUNT IS SHOWN. An audit tool that silently discards a third of
 * what it generated is a tool whose output nobody can calibrate; seeing "two
 * were thrown away" is how an operator learns to re-run rather than to trust a
 * thin list.
 */
import { Quote } from "lucide-react";
import type { BrainArgument, BrainFinding } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";
import { findingText } from "./findingText";

export function BrainArguments({
  synthesis,
  findings,
  t,
}: {
  synthesis: { arguments: BrainArgument[]; dropped: number } | null;
  findings: BrainFinding[];
  t: T;
}) {
  if (!synthesis) return <p className="text-sm text-muted-foreground">{t("brain.arguments.missing")}</p>;
  const byId = new Map(findings.map((f) => [f.id, f]));

  if (!synthesis.arguments.length) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">{t("brain.arguments.none")}</p>
        <Dropped count={synthesis.dropped} t={t} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ol className="space-y-2">
        {synthesis.arguments.map((argument) => (
          <li key={argument.id} className="space-y-1.5 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-3">
            <p className="flex gap-2 text-sm leading-snug">
              <Quote className="mt-0.5 size-3.5 shrink-0 text-primary" />
              <span className="font-medium">{argument.claim}</span>
            </p>
            <p className="text-sm leading-snug text-muted-foreground">{argument.businessImpact}</p>

            <ul className="space-y-0.5 border-t border-[var(--crm-hair)] pt-1.5">
              {argument.findingIds.map((id) => {
                const finding = byId.get(id);
                return (
                  <li key={id} className="truncate text-xs text-muted-foreground">
                    {finding ? findingText(finding, t) : id}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>

      <Dropped count={synthesis.dropped} t={t} />
    </div>
  );
}

function Dropped({ count, t }: { count: number; t: T }) {
  if (!count) return null;
  // `n`, not `count`: the plural picker reads `n` and nothing else, and the
  // interpolator leaves an unmatched placeholder standing on purpose. Passing
  // the wrong name printed the literal "{n}" on screen and always chose the
  // plural form. `catalogues.test.ts` now fails on the whole class of it.
  return <p className="text-xs text-muted-foreground">{t("brain.arguments.dropped", { n: count })}</p>;
}
